import { useEffect, useState, useCallback, useRef } from 'react';
import { listIdeas, ideasCounts, pushIdeas, skipIdeas, getCode, setCode, clearCode } from '../api/lionjob';
import LionjobPager from '../components/LionjobPager';

// Màn IDEAS (Lionjob) — khâu GIỮA phễu, quét từ matrixty /api/v1/ideas.
// Trending (phát hiện) -> IDEAS (kiểm bản quyền + chấm thiết kế) -> Scraped Products (làm SEO).
// Cổng quan trọng nhất: tmStatus. Đa số ý tưởng bên matrixty còn "unchecked".
const TM = [
  ['all', 'Tất cả'],
  ['unchecked', 'Chưa kiểm'],
  ['checking', 'Đang kiểm'],
  ['safe', 'An toàn'],
  ['approved', 'Đã duyệt'],
  ['risky', 'Rủi ro'],
];
const TM_LABEL = { unchecked: 'Chưa kiểm bản quyền', checking: 'Đang kiểm', safe: 'An toàn', approved: 'Đã duyệt', risky: 'Rủi ro bản quyền' };
const TM_CLS = { unchecked: 'mute', checking: 'info', safe: 'ok', approved: 'ok', risky: 'dang' };
const SORTS = [['newest', 'Mới nhất'], ['design', 'Điểm thiết kế cao nhất'], ['oldest', 'Cũ nhất']];

const fmt = (n) => (n == null ? '—' : new Intl.NumberFormat('vi-VN').format(n));

export default function IdeasPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [lockErr, setLockErr] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [tmStatus, setTmStatus] = useState('all');
  const [status, setStatus] = useState('new');
  const [variant, setVariant] = useState('all');
  const [sort, setSort] = useState('newest');
  const [q, setQ] = useState('');
  const [hasDesign, setHasDesign] = useState(false);
  const [hasNiche, setHasNiche] = useState(false);
  const [sel, setSel] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [extReady, setExtReady] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    const params = { page, limit: 24, sort, tmStatus, status, variant };
    if (q) params.q = q;
    if (hasDesign) params.hasDesign = '1';
    if (hasNiche) params.hasNiche = '1';
    try {
      const data = await listIdeas(params);
      setRows(data.rows); setMeta({ page: data.page, totalPages: data.totalPages, total: data.total });
      setUnlocked(true);
      ideasCounts().then(setCounts).catch(() => {});
    } catch (e) {
      const m = e.response?.data?.message || e.message || 'Không kết nối được backend';
      if (e.response?.status === 403) { clearCode(); setUnlocked(false); }
      setLockErr(m); setMsg('Lỗi: ' + m);
    }
  }, [page, sort, tmStatus, status, variant, q, hasDesign, hasNiche]);

  useEffect(() => { if (unlocked) load(); }, [load, unlocked]);
  useEffect(() => { if (getCode()) load(); /* eslint-disable-next-line */ }, []);

  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window || !ev.data || ev.data.source !== 'lionjob-ext') return;
      if (ev.data.type === 'ready') setExtReady(true);
      if (ev.data.type === 'scan-result') {
        setScanning(false);
        const r = ev.data.resp;
        if (r && r.ok) { setMsg(`✓ Quét ${r.scanned ?? r.received} ý tưởng · mới ${r.inserted}, cập nhật ${r.updated}`); loadRef.current?.(); }
        else setMsg('✗ ' + ((r && r.error) || 'Quét lỗi'));
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'lionjob-page', type: 'ping' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  function scan() {
    if (!extReady) { setMsg('Chưa thấy extension 🦁 — cài extension rồi mở tab matrixty.com.'); return; }
    setScanning(true); setMsg('Đang quét Ideas từ Matrixty…');
    window.postMessage({ source: 'lionjob-page', type: 'scan', kind: 'idea' }, '*');
    setTimeout(() => setScanning((s) => { if (s) setMsg('Quét quá lâu — kiểm tra tab matrixty.com.'); return false; }), 120000);
  }

  async function unlock(e) {
    e?.preventDefault();
    const c = codeInput.trim(); if (!c) { setLockErr('Nhập mã code'); return; }
    setLockErr(''); setCode(c); setUnlocking(true);
    try { await load(); } finally { setUnlocking(false); }
  }

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const clearSel = () => setSel([]);

  async function doPush(force = false) {
    if (!sel.length) return;
    setBusy(true);
    try {
      const r = await pushIdeas(sel, force);
      if (r.blocked?.length && !force) {
        const names = r.blocked.map((b) => `• ${b.title.slice(0, 60)} — ${b.reason}`).join('\n');
        if (window.confirm(`${r.blocked.length} ý tưởng có cảnh báo:\n\n${names}\n\nVẫn đẩy sang Scraped Products?`)) {
          const r2 = await pushIdeas(r.blocked.map((b) => b.id), true);
          setMsg(`✓ ${r.message} · ${r2.message}`);
        } else setMsg('✓ ' + r.message);
      } else setMsg('✓ ' + r.message);
      clearSel(); load();
    } catch (e) { setMsg('Lỗi đẩy: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(false); }
  }
  async function doSkip() {
    if (!sel.length) return;
    setBusy(true);
    try { const r = await skipIdeas(sel); setMsg('✓ ' + r.message); clearSel(); load(); }
    catch (e) { setMsg('Lỗi: ' + (e.response?.data?.message || e.message)); }
    finally { setBusy(false); }
  }

  if (!unlocked) {
    return (
      <div className="id-root"><style>{CSS}</style>
        <div className="id-lock"><div className="id-lockCard">
          <div className="id-lockIco">🦁</div><h2>Ideas</h2>
          <p className="id-lockSub">Khu vực chỉ dành cho Giám đốc. Nhập mã code để mở.</p>
          <form onSubmit={unlock}>
            <input className="id-lockInput" type="password" autoFocus placeholder="Mã code" disabled={unlocking}
              value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
            <button className="id-btn primary" type="submit" disabled={unlocking} style={{ width: '100%', marginTop: 10 }}>
              {unlocking ? '⏳ Đang kết nối…' : 'Mở khóa'}</button>
          </form>
          {lockErr && <div className="id-lockErr">⚠ {lockErr}</div>}
        </div></div>
      </div>
    );
  }

  const byTm = counts?.byTm || {};

  return (
    <div className="id-root"><style>{CSS}</style>
      <div className="id-head">
        <div>
          <h2>Ideas</h2>
          <div className="id-sub">{msg || 'Ý tưởng sản phẩm từ matrixty. Kiểm bản quyền và chấm thiết kế trước khi đẩy sang Scraped Products.'}</div>
        </div>
        <div className="id-sp" />
        <button className="id-btn primary" onClick={scan} disabled={scanning || !extReady}
          title={extReady ? 'Quét màn Ideas từ matrixty.com' : 'Cần cài extension 🦁'}>
          {scanning ? '⏳ Đang quét…' : '🦁 Quét Ideas'}
        </button>
      </div>

      <div className="id-strip">
        <Tile k="Đang xem" v={fmt(meta.total)} n={tmStatus === 'all' ? 'mọi trạng thái bản quyền' : TM_LABEL[tmStatus]} />
        <Tile k="Tổng ý tưởng" v={fmt(counts?.total)} n="đã quét về" />
        <Tile k="Chưa kiểm bản quyền" v={fmt(byTm.unchecked)} n="cần kiểm trước khi làm" flag={byTm.unchecked > 0} />
        <Tile k="Đã có thiết kế" v={fmt(counts?.withDesign)} n="có file design" />
        <Tile k="Chưa xử lý" v={fmt(counts?.unpushed)} n="còn lại để duyệt" />
      </div>

      <div className="id-filters">
        <div className="id-tmbar">
          {TM.map(([v, label]) => (
            <button key={v} className={'id-tm' + (tmStatus === v ? ' on' : '') + (v === 'risky' ? ' dang' : '')}
              onClick={() => { setTmStatus(v); setPage(1); clearSel(); }}>
              {label} <span className="id-c">{v === 'all' ? fmt(counts?.total) : fmt(byTm[v] || 0)}</span>
            </button>
          ))}
        </div>

        <div className="id-row">
          <input className="id-fld" style={{ minWidth: 190 }} placeholder="Tìm theo tên sản phẩm…"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <select className="id-fld" value={variant} onChange={(e) => { setVariant(e.target.value); setPage(1); }}>
            <option value="all">Tất cả loại SP</option>
            {(counts?.variants || []).map((v) => <option key={v.name} value={v.name}>{v.name} ({v.n})</option>)}
          </select>
          <select className="id-fld" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="all">Tất cả trạng thái</option>
            <option value="new">Chưa xử lý</option>
            <option value="pushed">Đã đẩy</option>
            <option value="skipped">Đã bỏ qua</option>
          </select>
          <select className="id-fld" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className={'id-pill' + (hasDesign ? ' on' : '')} onClick={() => { setHasDesign((v) => !v); setPage(1); }}>Đã có thiết kế</button>
          <button className={'id-pill' + (hasNiche ? ' on' : '')} onClick={() => { setHasNiche((v) => !v); setPage(1); }}>Đã có niche</button>
          <label className="id-chk"><input type="checkbox" checked={sel.length > 0 && sel.length === rows.length}
            onChange={(e) => (e.target.checked ? setSel(rows.map((r) => r.id)) : clearSel())} /> Chọn cả trang</label>
        </div>
      </div>

      <div className="id-grid">
        {rows.length === 0 && <div className="id-empty">Chưa có dữ liệu. Bấm <b>🦁 Quét Ideas</b> để lấy từ matrixty.com.</div>}
        {rows.map((r) => <Card key={r.id} r={r} sel={sel.includes(r.id)} onPick={() => toggle(r.id)} />)}
      </div>

      <LionjobPager page={meta.page} totalPages={meta.totalPages} onGo={setPage} />

      {sel.length > 0 && (
        <div className="id-bar">
          <span className="id-n">Đã chọn <span>{sel.length}</span></span>
          <button className="id-btn primary" onClick={() => doPush(false)} disabled={busy}>
            {busy ? '⏳ Đang đẩy…' : 'Đẩy sang Scraped Products'}</button>
          <button className="id-btn" onClick={doSkip} disabled={busy}>Đánh dấu đã xem</button>
          <button className="id-link" onClick={clearSel}>Bỏ chọn</button>
          <span className="id-note">Ý tưởng rủi ro hoặc chưa kiểm bản quyền sẽ được hỏi lại.</span>
        </div>
      )}
    </div>
  );
}

function Tile({ k, v, n, flag }) {
  return <div className={'id-tile' + (flag ? ' flag' : '')}>
    <div className="id-tk">{k}</div><div className="id-tv">{v}</div><div className="id-tn">{n}</div></div>;
}

function Card({ r, sel, onPick }) {
  // Sọc cảnh báo: đỏ nếu matrixty đánh dấu risky HOẶC tên dính nhãn hiệu mà chưa kiểm.
  const unchecked = r.tmStatus === 'unchecked' || r.tmStatus === 'checking';
  const danger = r.tmStatus === 'risky' || (r.riskIp && unchecked);
  const warn = r.riskNonPod;
  const img = r.thumbnail || (r.mockups || [])[0] || '';
  return (
    <article className={'id-card' + (sel ? ' sel' : '') + (danger ? ' risk-ip' : warn ? ' risk-nonpod' : '')}
      onClick={onPick} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}>
      <div className="id-shot">
        {img ? <img src={img} alt="" loading="lazy" /> : <div className="id-noimg" />}
        <div className="id-box">{sel ? '✓' : ''}</div>
        {r.designScore != null && <div className="id-score">{r.designScore}</div>}
        {(r.mockups || []).length > 0 && <div className="id-mk">{r.mockups.length} mockup</div>}
      </div>
      <div className="id-body">
        <div className="id-toprow">
          <span className={'id-chip ' + (TM_CLS[r.tmStatus] || 'mute')}>{TM_LABEL[r.tmStatus] || r.tmStatus}</span>
          {r.variant && <span className="id-var">{r.variant}</span>}
        </div>
        <div className="id-title">{r.productName || '(chưa có tên)'}</div>
        {r.sku && <div className="id-sku">{r.sku}</div>}

        <div className="id-meta">
          <span>{r.domain || '—'}</span>
          {r.niche && <span className="id-niche">{r.niche}</span>}
        </div>

        {r.tmDetail && <div className="id-tmdetail">{r.tmDetail.slice(0, 120)}</div>}

        <div className="id-foot">
          {r.designUrl && <a className="id-a" href={r.designUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>↗ Thiết kế</a>}
          {r.linkSpy && <a className="id-a" href={r.linkSpy} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>↗ Nguồn spy</a>}
          <span className="id-src">{r.ideaSource || ''}</span>
        </div>

        <div className="id-chips">
          {r.riskIp && unchecked && <span className="id-chip dang">Nghi bản quyền{r.ipMatch?.[0] ? ' · ' + r.ipMatch[0] : ''}</span>}
          {r.riskNonPod && <span className="id-chip warn">Không làm được POD</span>}
          {!r.riskNonPod && r.podKeywords?.length > 0 && <span className="id-chip ok">POD · {r.podKeywords[0]}</span>}
          {!r.niche && <span className="id-chip mute">Chưa có niche</span>}
          {!r.designUrl && <span className="id-chip mute">Chưa có thiết kế</span>}
          {r.status === 'pushed' && <span className="id-chip info">Đã đẩy</span>}
          {r.status === 'skipped' && <span className="id-chip mute">Đã bỏ qua</span>}
        </div>
      </div>
    </article>
  );
}

const CSS = `
.id-root{color:var(--text);padding-bottom:70px}
.id-head{display:flex;gap:12px;align-items:flex-start;margin-bottom:16px;flex-wrap:wrap}
.id-head h2{margin:0;font-size:20px}
.id-sub{color:var(--muted);font-size:12.5px;margin-top:3px;max-width:70ch}
.id-sp{flex:1}
.id-btn{background:var(--panel);color:var(--text);border:1px solid var(--border);padding:7px 13px;border-radius:9px;cursor:pointer;font-size:13px}
.id-btn:hover:not(:disabled){border-color:var(--accent)}
.id-btn:disabled{opacity:.45;cursor:not-allowed}
.id-btn.primary{background:var(--accent);border-color:var(--accent);color:#fff;font-weight:600}
.id-link{border:0;background:none;color:var(--muted);text-decoration:underline;cursor:pointer;font:inherit;font-size:13px;padding:6px 4px}

.id-strip{display:grid;grid-template-columns:repeat(auto-fit,minmax(148px,1fr));gap:10px;margin-bottom:16px}
.id-tile{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:11px 13px}
.id-tk{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:var(--muted);font-weight:600}
.id-tv{font-size:21px;font-weight:600;margin-top:3px;font-family:var(--font-mono)}
.id-tn{font-size:11px;color:var(--muted);margin-top:1px}
.id-tile.flag{border-color:var(--warn)}
.id-tile.flag .id-tv,.id-tile.flag .id-tk{color:var(--warn)}

.id-filters{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:2px 14px 13px;margin-bottom:14px}
.id-tmbar{display:flex;gap:4px;border-bottom:1px solid var(--line);margin-bottom:11px;overflow-x:auto}
.id-tm{border:0;background:none;font:inherit;font-size:13px;color:var(--muted);padding:11px 12px 9px;cursor:pointer;white-space:nowrap;border-bottom:2px solid transparent;display:flex;align-items:center;gap:7px}
.id-tm:hover{color:var(--text)}
.id-tm.on{color:var(--accent);border-bottom-color:var(--accent);font-weight:600}
.id-tm.dang.on{color:var(--danger);border-bottom-color:var(--danger)}
.id-c{font-family:var(--font-mono);font-size:11px;background:var(--input);color:var(--muted);border-radius:20px;padding:1px 7px}

.id-row{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.id-fld{border:1px solid var(--border);background:var(--input);color:var(--text);font:inherit;font-size:12px;padding:6px 10px;border-radius:8px}
.id-pill{border:1px solid var(--border);background:var(--panel);color:var(--muted);font:inherit;font-size:12px;padding:5px 11px;border-radius:20px;cursor:pointer}
.id-pill:hover{border-color:var(--accent);color:var(--text)}
.id-pill.on{background:var(--accent);border-color:var(--accent);color:#fff;font-weight:600}
.id-chk{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--muted);cursor:pointer}

.id-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(232px,1fr));gap:14px}
.id-empty{grid-column:1/-1;padding:50px;text-align:center;color:var(--muted);border:1px dashed var(--border);border-radius:var(--radius)}
.id-card{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;cursor:pointer;position:relative;display:flex;flex-direction:column;transition:border-color .12s,transform .12s}
.id-card:hover{border-color:var(--accent);transform:translateY(-1px)}
.id-card.sel{border-color:var(--accent);box-shadow:0 0 0 2px color-mix(in srgb,var(--accent) 30%,transparent)}
.id-card.risk-ip::before,.id-card.risk-nonpod::before{content:"";position:absolute;inset:0 0 auto;height:3px;z-index:3}
.id-card.risk-ip::before{background:var(--danger)}
.id-card.risk-nonpod::before{background:var(--warn)}

.id-shot{position:relative;aspect-ratio:1/1;background:var(--input);overflow:hidden}
.id-shot img{width:100%;height:100%;object-fit:cover;display:block}
.id-noimg{width:100%;height:100%}
.id-box{position:absolute;top:9px;left:9px;width:19px;height:19px;border-radius:5px;border:1.5px solid rgba(255,255,255,.9);background:rgba(15,20,35,.35);display:grid;place-items:center;color:#fff;font-size:12px;z-index:2}
.id-card.sel .id-box{background:var(--accent);border-color:var(--accent)}
.id-score{position:absolute;top:9px;right:9px;z-index:2;font-family:var(--font-mono);font-size:12px;font-weight:700;color:#fff;background:var(--accent);border-radius:20px;padding:2px 9px}
.id-mk{position:absolute;left:9px;bottom:9px;z-index:2;font-size:10px;color:#fff;background:rgba(15,20,35,.6);border-radius:6px;padding:2px 7px}

.id-body{padding:10px 12px 11px;display:flex;flex-direction:column;gap:6px;flex:1}
.id-toprow{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.id-var{font-size:10.5px;color:var(--muted);border:1px solid var(--border);border-radius:5px;padding:1px 6px}
.id-title{font-size:12.5px;font-weight:600;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.id-sku{font-family:var(--font-mono);font-size:10.5px;color:var(--muted)}
.id-meta{display:flex;gap:6px;align-items:center;font-size:11px;color:var(--muted);flex-wrap:wrap}
.id-niche{color:var(--accent);border:1px solid var(--accent);border-radius:5px;padding:0 5px;font-size:10px}
.id-tmdetail{font-size:11px;color:var(--muted);background:var(--input);border-radius:6px;padding:5px 7px;max-height:52px;overflow:hidden}
.id-foot{display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:auto;padding-top:2px}
.id-a{font-size:11px;color:var(--info);text-decoration:none}
.id-a:hover{text-decoration:underline}
.id-src{font-size:10.5px;color:var(--muted);margin-left:auto;font-family:var(--font-mono)}
.id-chips{display:flex;gap:5px;flex-wrap:wrap}
.id-chip{font-size:10.5px;font-weight:600;border-radius:5px;padding:2px 7px;border:1px solid var(--border);color:var(--muted)}
.id-chip.ok{color:var(--ok);border-color:var(--ok)}
.id-chip.warn{color:var(--warn);border-color:var(--warn)}
.id-chip.dang{color:var(--danger);border-color:var(--danger)}
.id-chip.info{color:var(--info);border-color:var(--info)}

.id-bar{position:sticky;bottom:0;background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:11px 16px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:14px;z-index:20}
.id-n{font-size:13px;font-weight:600}
.id-n span{font-family:var(--font-mono);color:var(--accent)}
.id-note{font-size:12px;color:var(--muted);margin-left:auto}

.id-lock{display:flex;justify-content:center;padding:60px 20px}
.id-lockCard{background:var(--panel);border:1px solid var(--border);border-radius:var(--radius);padding:28px 26px;width:340px;text-align:center}
.id-lockIco{font-size:38px;line-height:1}.id-lockCard h2{margin:8px 0 4px}
.id-lockSub{color:var(--muted);font-size:13px;margin:0 0 16px}
.id-lockInput{width:100%;background:var(--input);color:var(--text);border:1px solid var(--border);border-radius:10px;padding:10px 12px;font-size:14px;text-align:center;letter-spacing:2px}
.id-lockErr{color:var(--danger);font-size:12px;margin-top:10px}
`;
